import { useEffect, useState, useSyncExternalStore } from 'react'
import { useIsAuthenticated } from '@azure/msal-react'
import { useTenant } from '@/contexts/TenantContext'
import { useMyWorkflowRunActivity } from '@/hooks/useMyWorkflowRunActivity'
import { useWorkerInterval } from '@/hooks/useWorkerInterval'
import {
  buildIndicatorFavicon,
  DEFAULT_FAVICON_HREF,
  DEFAULT_TAB_TITLE,
  PULSE_FRAME_MS,
  resolveTabIndicator,
  tabTitle,
} from '@/lib/tabActivityIndicator'
import type { WorkflowRunActivityState } from '@/types'

const ACTIVE_POLL_MS = 10_000
const IDLE_POLL_MS = 60_000
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

interface ObservedActivity {
  tenantId?: string
  state?: WorkflowRunActivityState
  finished: boolean
}

function subscribeToReducedMotion(onChange: () => void) {
  const query = window.matchMedia(REDUCED_MOTION_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function prefersReducedMotion() {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

function isTabAttended() {
  return document.visibilityState === 'visible' && document.hasFocus()
}

function faviconLink() {
  return document.querySelector<HTMLLinkElement>('link[rel="icon"]')
}

function restoreDefaults() {
  const link = faviconLink()
  if (link) {
    link.setAttribute('href', DEFAULT_FAVICON_HREF)
    delete link.dataset.activityState
    delete link.dataset.activityMotion
  }
  document.title = DEFAULT_TAB_TITLE
}

export function TabActivityIndicator() {
  const isAuthenticated = useIsAuthenticated()
  const { currentTenant } = useTenant()
  const tenantId = currentTenant?.id
  const enabled = isAuthenticated && !!tenantId
  const { data, refetch } = useMyWorkflowRunActivity(enabled)
  const state = enabled ? data?.state : undefined

  const [observed, setObserved] = useState<ObservedActivity>({ finished: false })
  if (state && (observed.tenantId !== tenantId || observed.state !== state)) {
    const sameTenant = observed.tenantId === tenantId
    setObserved({
      tenantId,
      state,
      finished: sameTenant && (observed.finished || (observed.state !== 'Idle' && state === 'Idle' && !isTabAttended())),
    })
  }

  const reducedMotion = useSyncExternalStore(subscribeToReducedMotion, prefersReducedMotion)
  const indicator = enabled ? resolveTabIndicator(state, observed.tenantId === tenantId && observed.finished) : null
  const animated = indicator === 'running' && !reducedMotion
  const [frame, setFrame] = useState(0)

  useWorkerInterval(() => { void refetch() }, enabled ? (state && state !== 'Idle' ? ACTIVE_POLL_MS : IDLE_POLL_MS) : null)
  useWorkerInterval(() => setFrame(f => f + 1), animated ? PULSE_FRAME_MS : null)

  useEffect(() => {
    const acknowledge = () => {
      if (isTabAttended()) setObserved(o => (o.finished ? { ...o, finished: false } : o))
    }
    window.addEventListener('focus', acknowledge)
    document.addEventListener('visibilitychange', acknowledge)
    return () => {
      window.removeEventListener('focus', acknowledge)
      document.removeEventListener('visibilitychange', acknowledge)
    }
  }, [])

  useEffect(() => {
    if (!indicator) {
      restoreDefaults()
      return
    }
    const link = faviconLink()
    if (link) {
      link.setAttribute('href', buildIndicatorFavicon(indicator, animated ? frame : 0))
      link.dataset.activityState = indicator
      link.dataset.activityMotion = animated ? 'pulse' : 'static'
    }
    document.title = tabTitle(indicator)
  }, [indicator, animated, frame])

  useEffect(() => restoreDefaults, [])

  return null
}
